/* hydrus-apparel.js — Phase 3K (Phase 3L revised)
 *
 * Document-level event delegation for the apparel chrome on product.simple
 * PDPs. Singleton-guarded; safe to load on non-apparel simple products
 * (inits silently with no matching DOM).
 *
 * Responsibilities:
 *   1. Size guide modal open/close (native <dialog> .showModal() / .close()).
 *   2. Swatch click → re-evaluate size-button availability against the
 *      newly-selected color's variants (delta 8). OOS sizes get
 *      disabled + aria-disabled="true" + aria-label="Size <N>, out of stock"
 *      (delta 9). Low-stock "Only N left" copy updates inline against
 *      settings.apparel_low_stock_threshold.
 *   3. Sticky mobile ATC:
 *      - IntersectionObserver on the main ATC toggles .visible on scroll.
 *      - Variant label + price update on any option change.
 *      - Sticky button delegates click to the main ATC (form submit).
 *   4. Dispatch the `hydrus:variant-option-change` custom event on any
 *      option change (not just swatches). hydrus-gallery.js subscribes
 *      to this event for variant-image swap. This file no longer owns
 *      gallery behavior — ownership moved to hydrus-gallery.js in
 *      Phase 3L, decoupled via the custom event.
 *
 * Data source: each buybox section emits a
 *   window._hydrusApparel_<sectionId>
 * global carrying { optionNames, variants, lowStockThreshold, currencySymbol }.
 * The JS discovers sections by scanning for this global name prefix on init.
 *
 * Interaction with hydrus-variant-reactivity.js: both listen on form
 * change; neither depends on the other. Reactivity fetches the reactive
 * zone (ATC + inventory state). This file mutates DOM outside the
 * reactive zone (size buttons, sticky bar) and dispatches the option-
 * change event. hydrus-gallery.js owns the gallery. No race, no shared
 * state.
 */
(function () {
  if (window.HydrusApparel) return;
  window.HydrusApparel = true;

  const PREFIX = '_hydrusApparel_';

  // ─── data access ───────────────────────────────────────────────
  function getSection(sectionId) {
    return window[PREFIX + sectionId] || null;
  }

  function getSelectedOptions() {
    // Returns { optionName: selectedValue } across all product option radios
    // on the page. Good enough because Hydrus PDPs never host two product
    // forms simultaneously.
    const sel = {};
    document.querySelectorAll('input[type="radio"][name^="options["]:checked').forEach(r => {
      const match = r.name.match(/^options\[(.+)\]$/);
      if (match) sel[match[1]] = r.value;
    });
    return sel;
  }

  function findVariant(data, selectedOptions) {
    return data.variants.find(v => {
      for (let i = 0; i < data.optionNames.length; i++) {
        const name = data.optionNames[i];
        const expected = selectedOptions[name];
        const actual = v['option' + (i + 1)];
        if (expected !== actual) return false;
      }
      return true;
    });
  }

  // ─── size-button availability ──────────────────────────────────
  function updateSizeAvailability(sectionId) {
    const data = getSection(sectionId);
    if (!data) return;
    const container = document.querySelector(
      `[data-hydrus-size-buttons][data-section-id="${sectionId}"]`
    );
    if (!container) return;

    const sel = getSelectedOptions();
    const threshold = Number(data.lowStockThreshold || 0);

    container.querySelectorAll('label[data-size-value]').forEach(label => {
      const size = label.dataset.sizeValue;
      const input = label.querySelector('input[type="radio"][data-hydrus-size-input]');
      if (!input) return;

      // Trial options: copy current selection, overlay the candidate size
      const optName = (input.name.match(/^options\[(.+)\]$/) || [])[1];
      if (!optName) return;
      const trial = Object.assign({}, sel, { [optName]: size });
      const variant = findVariant(data, trial);

      let available = false, qty = 0, tracked = false;
      if (variant) {
        available = !!variant.available;
        qty = Number(variant.qty || 0);
        tracked = !!variant.tracked;
      }

      label.classList.toggle('simple-size-btn-label--oos', !available);
      input.disabled = !available;
      input.setAttribute('aria-disabled', available ? 'false' : 'true');
      input.setAttribute('aria-label', available ? 'Size ' + size : 'Size ' + size + ', out of stock');

      // Low-stock inline copy (delta 4)
      let lowEl = label.querySelector('.simple-size-btn-lowstock');
      const showLow = tracked && available && threshold > 0 && qty > 0 && qty <= threshold;
      if (showLow) {
        if (!lowEl) {
          lowEl = document.createElement('span');
          lowEl.className = 'simple-size-btn-lowstock';
          label.appendChild(lowEl);
        }
        lowEl.textContent = 'Only ' + qty + ' left';
        lowEl.setAttribute('aria-label', ', only ' + qty + ' left');
      } else if (lowEl) {
        lowEl.remove();
      }
    });
  }

  // Gallery → color sync moved to hydrus-gallery.js in Phase 3L.
  // This file dispatches the generalized `hydrus:variant-option-change`
  // custom event in the change handler below; the gallery listens for
  // the event and handles #<option>:<Label> alt-text matching.

  // ─── sticky ATC ────────────────────────────────────────────────
  function formatMoneyCents(cents, sym) {
    const dollars = cents / 100;
    const fixed = cents % 100 === 0 ? dollars.toFixed(0) : dollars.toFixed(2);
    return (sym || '$') + fixed.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  function updateStickyATC(sectionId) {
    const root = document.getElementById('simple-sticky-atc-' + sectionId);
    if (!root) return;

    const data = getSection(sectionId);
    const sel = getSelectedOptions();
    const variant = data ? findVariant(data, sel) : null;

    const variantEl = root.querySelector('[data-hydrus-sticky-variant]');
    const priceEl = root.querySelector('[data-hydrus-sticky-price]');
    const btnEl = root.querySelector('.simple-sticky-btn');

    if (variantEl) {
      if (data && data.optionNames && data.optionNames.length > 0) {
        // Skip Shopify's "Default Title" placeholder per the same rule
        // applied in snippets/hydrus-apparel-sticky-atc.liquid's Liquid
        // builder. Keeps display consistent across initial render + live
        // option toggles.
        const parts = [];
        data.optionNames.forEach(name => {
          const v = sel[name];
          if (v && v !== 'Default Title') parts.push(v);
        });
        variantEl.textContent = parts.join(' · ');
      }
    }
    if (priceEl && variant && typeof variant.price === 'number') {
      priceEl.textContent = formatMoneyCents(variant.price, (data && data.currencySymbol) || '$');
    }
    if (btnEl) {
      if (variant) {
        btnEl.disabled = !variant.available;
      }
    }
  }

  function initStickyATC(sectionId) {
    const mainAtc = document.querySelector('#simple-reactive-' + sectionId + ' .simple-atc');
    const sticky = document.getElementById('simple-sticky-atc-' + sectionId);
    if (!mainAtc || !sticky) return;
    const obs = new IntersectionObserver(entries => {
      entries.forEach(e => { sticky.classList.toggle('visible', !e.isIntersecting); });
    }, { threshold: 0 });
    obs.observe(mainAtc);
  }

  // ─── event delegation: click ───────────────────────────────────
  document.addEventListener('click', function (e) {
    // Size guide modal open
    const openBtn = e.target.closest('[data-hydrus-size-guide-open]');
    if (openBtn) {
      e.preventDefault();
      const targetId = openBtn.getAttribute('aria-controls');
      const modal = targetId
        ? document.getElementById(targetId)
        : document.querySelector('.hydrus-size-guide-modal');
      if (modal && typeof modal.showModal === 'function') {
        try { modal.showModal(); } catch (err) { /* already open */ }
      }
      return;
    }
    // Size guide modal close (X button)
    const closeBtn = e.target.closest('[data-hydrus-size-guide-close]');
    if (closeBtn) {
      e.preventDefault();
      const modal = closeBtn.closest('dialog');
      if (modal && typeof modal.close === 'function') modal.close();
      return;
    }
    // Size guide modal close (click on backdrop — target IS the dialog itself)
    if (e.target.matches('dialog.hydrus-size-guide-modal')) {
      e.target.close();
      return;
    }
    // Sticky mobile ATC button → click the main ATC (form submit path)
    const stickyBtn = e.target.closest('.simple-sticky-btn');
    if (stickyBtn) {
      e.preventDefault();
      const root = stickyBtn.closest('[id^="simple-sticky-atc-"]');
      if (!root) return;
      const sectionId = root.id.replace('simple-sticky-atc-', '');
      const mainAtc = document.querySelector('#simple-reactive-' + sectionId + ' .simple-atc');
      if (mainAtc) mainAtc.click();
    }
  });

  // ─── event delegation: change ──────────────────────────────────
  document.addEventListener('change', function (e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (input.type !== 'radio' || !input.name || input.name.indexOf('options[') !== 0) return;

    // Find this section's id from the nearest data-section-id carrier
    const carrier = input.closest('[data-hydrus-swatches], [data-hydrus-size-buttons]');
    let sectionId = carrier ? carrier.dataset.sectionId : null;

    // Dispatch the generalized option-change event for any option radio
    // (swatch or size or future-other). hydrus-gallery.js consumes this
    // for variant-image swap via the #<option>:<Label> alt convention;
    // any other subscriber (future reactive zones, analytics, etc.) can
    // listen too. The raw option name from the input's `name` attribute
    // drives the event — it's what the admin sees in Shopify, matching
    // the hydrus_hero.variant_group_key metafield case-insensitively.
    const optMatch = input.name.match(/^options\[(.+)\]$/);
    const optName = optMatch ? optMatch[1] : null;
    const optValue = input.dataset.swatchLabel || input.value;
    if (optName) {
      document.dispatchEvent(new CustomEvent('hydrus:variant-option-change', {
        detail: { option: optName, value: optValue, sectionId: sectionId }
      }));
    }

    // If it's a swatch change: update size availability + swatch label.
    // Gallery swap is handled by the event dispatched above.
    if (input.hasAttribute('data-hydrus-swatch-input')) {
      if (sectionId) updateSizeAvailability(sectionId);
      const label = optValue;
      // Toggle active class on the parent label
      const host = input.closest('[data-hydrus-swatches]');
      if (host) {
        host.querySelectorAll('.simple-swatch').forEach(s => s.classList.remove('simple-swatch--active'));
        const thisLabel = input.closest('.simple-swatch');
        if (thisLabel) thisLabel.classList.add('simple-swatch--active');
      }
      // Update the inline color label in the size picker legend
      const legend = document.getElementById('simple-color-label-' + sectionId);
      if (legend) legend.textContent = label;
    }

    // If it's a size change: update active class, update sticky
    if (input.hasAttribute('data-hydrus-size-input')) {
      const host = input.closest('[data-hydrus-size-buttons]');
      if (host) {
        host.querySelectorAll('.simple-size-btn-label').forEach(l => l.classList.remove('simple-size-btn-label--active'));
        const thisLabel = input.closest('.simple-size-btn-label');
        if (thisLabel) thisLabel.classList.add('simple-size-btn-label--active');
      }
    }

    // Any option change: update sticky ATC label + price for all sections on page
    document.querySelectorAll('[id^="simple-sticky-atc-"]').forEach(root => {
      const sid = root.id.replace('simple-sticky-atc-', '');
      updateStickyATC(sid);
    });
  });

  // ─── init on DOM ready ─────────────────────────────────────────
  function initAll() {
    Object.keys(window).forEach(key => {
      if (key.indexOf(PREFIX) !== 0) return;
      const sectionId = key.slice(PREFIX.length);
      initStickyATC(sectionId);
      updateSizeAvailability(sectionId);
      updateStickyATC(sectionId);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }
})();
